import { Stack, Theme } from '@mui/material';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormAutocompleteProps, FormLabel } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { toSxArray } from 'src/utils/sx';
import { getProjectStageIcon } from './get-project-stage-icon';

export type ProjectStageFormValue = { id: string; label: string };

export const ProjectStageSelectMenuField = ({
  field,
  disabled,
}: {
  field: FormAutocompleteProps;
  disabled?: boolean;
}) => {
  const stageOptions = useMemo(
    () => (field.options ?? []) as ProjectStageFormValue[],
    [field.options],
  );

  const selectMenuOptions = useMemo<SelectMenuOption[]>(
    () =>
      stageOptions.map((option) => ({
        value: String(option.id),
        title: option.label,
        icon: getProjectStageIcon(Number(option.id)),
      })),
    [stageOptions],
  );

  return (
    <Stack direction="row" alignItems="center" px={2} key={field.label}>
      <FormLabel label={field.label} />
      <Stack direction="row" width="100%" gap={1}>
        <Controller
          name={field.name}
          rules={{ required: field.required }}
          render={({ field: controllerField }) => {
            const value = (controllerField.value ??
              field.defaultValue ??
              null) as ProjectStageFormValue | null;

            return (
              <SelectMenu
                label={field.label ?? 'Stage'}
                options={selectMenuOptions}
                value={value?.id ? String(value.id) : null}
                onChange={(selectedId) => {
                  const selectedOption = stageOptions.find(
                    (option) => String(option.id) === selectedId,
                  );

                  if (selectedOption) {
                    controllerField.onChange(selectedOption);
                  }
                }}
                triggerIcon={value?.id ? getProjectStageIcon(Number(value.id)) : undefined}
                disabled={Boolean(
                  disabled || field.disabled || field.autocompleteProps?.disabled,
                )}
                search
                searchPlaceholder="Search stages"
                emptyText={
                  field.autocompleteProps?.loading ? 'Loading stages...' : 'No stages found.'
                }
                variant="inline"
                plainOptionIcons
                sx={[
                  { width: '100%' },
                  ...toSxArray(field.inputProps?.sx),
                  (theme: Theme) => ({
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.utils.important('1rem'),
                    },
                  }),
                ]}
              />
            );
          }}
        />
      </Stack>
    </Stack>
  );
};
